/**
 * Manage breakpoints events
 */
const DeviceManager = {
    oldDevice: null,
    currentDevice: null,
    devices: {
        xl: 1200,
        lg: 992,
        md: 768,
        sm: 576,
        xs: 0,
    },
    callbacks: [],
    windowCallbacks: {},
    oldWindowWidth: 0,

    /**
     * Wait for DOM Content Loaded
     */
    init() {
        this.oldWindowWidth = window.innerWidth;

        if (document.readyState != 'loading') {
            this.initEvents();
        } else {
            document.addEventListener('DOMContentLoaded', () => {
                this.initEvents();
            });
        }
    },

    /**
     * Initialize events
     */
    initEvents() {
        this.checkDevice().then(attr => {
            this.executeCallbacks(attr);
            this.executeWindowCallbacks();
        }).catch(e => null);

        window.addEventListener('resize', () => {
            if (window.innerWidth !== this.oldWindowWidth) {
                this.oldWindowWidth = window.innerWidth;
                
                this.checkDevice().then(attr => {
                    this.executeWindowCallbacks();
                    this.executeCallbacks(attr);
                }).catch(e => null);
            }
        });
    },

    /**
     * Check if device has changed
     * @return {Promise}
     */
    checkDevice() {
        const winW = window.innerWidth;

        return new Promise((resolve, reject) => {
            if (this.oldDevice) {
                this.oldDevice = this.currentDevice;
            }

            let breakpointSup = 99999;
            for (let device in this.devices) {
                // Skip loop if the property is from prototype
                if (!this.devices.hasOwnProperty(device)) {
                    continue;
                }
            
                const breakpoint = this.devices[device];

                if (winW >= breakpoint && winW < breakpointSup && this.oldDevice !== device) {
                    this.currentDevice = device;

                    resolve({ devices: this.devices, current: device, old: this.oldDevice });
                }

                breakpointSup = this.devices[device];
            }

            reject();
        });
    },

    /**
     * Get the current device size
     */
    getCurrentDeviceSize() {
        return this.devices[this.getCurrentDevice()];
    },
    
    /**
     * Get the current device name
     */
    getCurrentDevice() {
        return this.currentDevice;
    },

    /**
     * Add a callback when device change
     * @param {CallableFunction} fn 
     */
    addCallback(fn) {
        this.callbacks.push(fn);
    },

    /**
     * Add a callback when window is resized
     * @param {string} name optional
     * @param {CallableFunction} fn required
     * @param {mixed} args optional
     * 
     * Usage:
     * addWindowCallback('callback-name', function, arg_1_for_function, arg_2_for_function, ...);
     */
    addWindowCallback(...args) {
        let name = null;
        let fn = null;

        if (typeof args[0] === 'string') {
            name = args[0];

            if (typeof args[1] === 'function') {
                fn = args[1];
            }
        } else if (typeof args[0] === 'function') {
            fn = args[0];

            let newName = 'callback-' + Object.keys(this.windowCallbacks).length;
            if (this.windowCallbacks[newName]) {
                newName += '1';
            }

            name = newName;
        }

        let newArgs = [];
        for (let i = 2; i < args.length; i++) {
            newArgs.push(args[i]);
        }

        if (!fn) {
            console.error('Function not defined');
            throw 'Function not defined';
        }

        this.windowCallbacks[name] = {
            fn,
            args: newArgs,
        };
    },

    /**
     * Remove a callback from window callbacks array
     * @param {string} name 
     */
    removeWindowCallback(name) {
        if (this.windowCallbacks[name]) {
            delete this.windowCallbacks[name];
        }
    },

    /**
     * Execute all callbacks
     * @param {Object} attr 
     */
    executeCallbacks(attr) {
        this.callbacks.forEach(callback => {
            callback(attr);
        });
    },

    /**
     * Execute all window callbacks
     */
    executeWindowCallbacks() {
        Object.keys(this.windowCallbacks).forEach(key => {
            const callback = this.windowCallbacks[key].fn;
            const args = this.windowCallbacks[key].args;
            
            callback({ devices: this.devices, current: this.currentDevice, old: this.oldDevice }, ...args);
        });
    },

    /**
     * Check if deviceToCompare is greater than deviceReference
     * @param {string} deviceToCompare 
     * @param {string} deviceReference 
     */
    isGreaterThan(deviceToCompare, deviceReference) {
        return this.devices[deviceToCompare] >= this.devices[deviceReference];
    }
};

export default DeviceManager;